import { Injectable } from '@angular/core';

/**
 * Platform-agnostic camera capture surface.
 *
 * Web implementation: uses a hidden `<input type="file" accept="image/*" capture="environment">`
 * that the caller mounts in its template. Browsers honour `capture` as a hint
 * (mobile Safari / mobile Chrome open the camera; desktop ignores it).
 *
 * Native implementation (`camera-capture.service.native.ts`) swaps in `@capacitor/camera`
 * at build time via Angular's `fileReplacements`, giving us a true native camera UI
 * with proper permission handling on iOS and Android.
 *
 * Consumers get a `File` back in both implementations so downstream code
 * (FileReader, upload, preview) works unchanged.
 */
@Injectable({ providedIn: 'root' })
export class CameraCaptureService {

  /**
   * Prompts the user to take a photo with the camera.
   * @param input optional hidden `<input type="file">` element to click.
   *              If not provided the service creates an ephemeral one.
   * @returns the captured `File`, or null if the user cancelled.
   */
  async capturePhoto(input?: HTMLInputElement): Promise<File | null> {
    if (typeof document === 'undefined') return null;

    const el = input ?? this.ephemeralInput();
    el.setAttribute('accept', 'image/*');
    el.setAttribute('capture', 'environment');

    return new Promise<File | null>((resolve) => {
      const onChange = () => {
        el.removeEventListener('change', onChange);
        const file = el.files && el.files.length ? el.files[0] : null;
        resolve(file);
      };
      el.addEventListener('change', onChange);
      el.click();
    });
  }

  isNative(): boolean {
    return false;
  }

  private ephemeralInput(): HTMLInputElement {
    const el = document.createElement('input');
    el.type = 'file';
    el.style.display = 'none';
    document.body.appendChild(el);
    return el;
  }
}
